import { useCallback, useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import ValuationSidebarSuperVisor from "./valuationSidebarInstututional";
import { useParams } from "react-router-dom";
import PropertyMap from "../map/PropertyMap";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Minus,
  Plus,
  UserPlus
} from "lucide-react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import ValuationVisualization from "../graphics/ValuationVisualization";
import PropertyDashboard from "../graphics/PropertyDashboard";
import PropertyInfographic from "../graphics/PropertyInfographic";
import PropertyValuationDashboard from "../graphics/PropertyValuationDashboard";
import ValuationTimeline from "../graphics/ValuationTimeline";
import { Property } from "../../types/propertyTypes";
import { getAllPropertyImages } from "@/utils/getAllPropertiesImage";
import { generateProfessionalPdfReport } from "@/utils/pdfReportGenerator";
import TimeTracker from "./PropertyValuationTracker";
import { uploadToCloudinary } from "../../utils/uploadToCloudinary";

interface Collector {
  id: string;
  lastName: string;
  firstName?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  profileImageUrl?: string | null;
}

interface AuthContextType {
  currentUser: {
    name: string;
    role: string;
  };
}

// Add these interfaces
interface ValuationTaskDetails {
  institutionalId: string;
  valuationAssignmentDate: string | Date;
  professionalCert: string;
  principalValuer: string;
  assistantValuer: string;
  regulatoryBody: string;
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}


// Base URL for API
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

// Default Property object
const defaultProperty: Property = {
  id: "",
  ownerName: "",
  ownerIdNumber: "",
  location: {
    district: "",
    sector: "",
    cell: "",
    village: "",
  },
  upi: "",
  landParcel: {
    area: 0,
    landUse: "",
    slopeCategory: "",
    accessToRoad: false,
  },
  mainBuilding: {
    constructedArea: 0,
    constructionYear: 0,
    numberOfFloors: 0,
    constructionQuality: "",
    exteriorFinish: "",
    roofType: "",
    maintenanceCondition: "",
  },
  amenities: {
    hasElectricity: false,
    hasWater: false,
    hasSewage: false,
    hasInternet: false,
    hasFence: false,
    hasGarden: false,
    hasParkingSpace: false,
  },
  buildingComponents: {},
  adjustmentsAndVariables: {},
  buildingDescription: {},
  propertyDetails: {},
  propertyPhotos: [],
  valuationDetails: {
    status: "Pending",
    landValue: 0,
    buildingValue: 0,
    amenitiesValue: 0,
    fenceLength: 0,
    verandahRate: 0,
    verandahArea: 0,
    entranceGateRate: 0,
    entranceGateLength: 0,
    depreciationValue: 0,
    totalPropertyValue: 0,
    valuationDate: "",
    collectorNotes: "",
    approvedBy: null,
    approvedDate: null,
    assignedCollector: "",
  },
};

// Helper function to format dates for input fields
const formatDateForInput = (
  dateString: string | Date | undefined | null,
): string => {
  if (!dateString) return "";

  try {
    const date =
      typeof dateString === "string" ? new Date(dateString) : dateString;

    // Format as YYYY-MM-DD for date input
    const formatted = date.toISOString().split("T")[0];
    return formatted || "";
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// PropertyDetails Component
interface PropertyDetailsProps {
  supervisorMode?: boolean;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  supervisorMode = false,
}) => {
  const { id: propertyId } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State management
  const [property, setProperty] = useState<Property>(defaultProperty);
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const { logout, token: authToken } = useAuth();
  const [isApproving, setIsApproving] = useState(false);
  const [isCollectorModalOpen, setIsCollectorModalOpen] = useState(false);
  const [selectedCollector, setSelectedCollector] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [zoomImage, setZoomImage] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 4;

  // Image gallery states
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [filter, setFilter] = useState<"data" | "graphics">("data");
  const { theme, setTheme } = useTheme();
  const isUganda =
    !!property?.location?.region ||
    (property as any)?.location?.country === "UG";
  const currency = isUganda ? "UGX" : " RWF";
  // Valuation task details state
  const [valuationTaskDetails, setValuationTaskDetails] =
    useState<ValuationTaskDetails>({
      institutionalId: "",
      valuationAssignmentDate: new Date(),
      professionalCert: "",
      principalValuer: "",
      assistantValuer: "",
      regulatoryBody: "",
    });

  const [institutionalClients, setInstitutionalClients] = useState<User[]>([]);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isClientsLoaded, setIsClientsLoaded] = useState(false);

  // Get all property images
  const propertyImages = getAllPropertyImages(property);
  const hasImages = propertyImages.length > 0;

  // Navigation functions
  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  // Image gallery handlers
  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % propertyImages.length);
    setZoomLevel(1);
  };

  const prevImage = () => {
    setSelectedImageIndex(
      (prev) => (prev - 1 + propertyImages.length) % propertyImages.length,
    );
    setZoomLevel(1);
  };

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
    setZoomLevel(1);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  // Fetch property data from API
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId) return;
      setIsLoading(true);
      setErrorMessage("");
      try {
        const Valuation_token = localStorage.getItem("valuation_token");
        const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Valuation_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch property: ${response.statusText}`);
        }

        const result = await response.json();
        setProperty(result.data);

        // Update valuation task details when property loads
        if (result.data.valuationTaskDetails) {
          setValuationTaskDetails(result.data.valuationTaskDetails);
        }
      } catch (error) {
        console.error("Error fetching property:", error);
        setErrorMessage("Failed to load property details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, isAssigning]);

  // Fetch collectors from API
  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        const Valuation_token = localStorage.getItem("valuation_token");
        const response = await fetch(`${BASE_URL}/users`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Valuation_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch collectors: ${response.statusText}`);
        }

        const result = await response.json();
        const users: User[] = result.data || [];
        const dataCollectors = users.filter(
          (user) => user.role === "DATA_COLLECTOR",
        );
        const collectorArray: Collector[] = dataCollectors.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
        }));
        setCollectors(collectorArray);
      } catch (error) {
        console.error("Error fetching collectors:", error);
      }
    };
    fetchCollectors();
  }, []);

  // Fetch institutional clients — FIX: added auth token
  useEffect(() => {
    const fetchInstitutionalClients = async () => {
      try {
        const Valuation_token = localStorage.getItem("valuation_token");
        const response = await fetch(`${BASE_URL}/users`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Valuation_token}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch users");
        const result = await response.json();
        const users: User[] = result.data || [];
        const clients = users.filter(
          (user) => user.role === "INSTITUTIONAL_CLIENT",
        );
        setInstitutionalClients(clients);
        setIsClientsLoaded(true);
      } catch (error) {
        console.error("Error fetching institutional clients:", error);
        setIsClientsLoaded(true);
      }
    };
    fetchInstitutionalClients();
  }, []);

  // Update valuation task details when property loads
  useEffect(() => {
    if (property.valuationTaskDetails) {
      // Ensure dates are properly converted
      const taskDetails = { ...property.valuationTaskDetails };

      // Convert string dates to Date objects if needed
      if (
        taskDetails.valuationAssignmentDate &&
        typeof taskDetails.valuationAssignmentDate === "string"
      ) {
        taskDetails.valuationAssignmentDate = new Date(
          taskDetails.valuationAssignmentDate,
        );
      }

      setValuationTaskDetails(taskDetails);
    }
  }, [property.valuationTaskDetails]);

  // Filtered collectors based on search query
  const filteredCollectors = collectors.filter((collector) =>
    collector.lastName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Filtered clients based on search query
  const filteredClients = institutionalClients.filter((client) =>
    `${client.firstName} ${client.lastName}`
      .toLowerCase()
      .includes(clientSearchQuery.toLowerCase()),
  );

  /* ================= UPLOAD REPORT ================= */
  const token = localStorage.getItem("valuation_token");
  const handleUploadReport = async () => {
    if (!token) {
      setErrorMessage("Authentication token missing. Please log in again.");
      return;
    }

    if (!selectedFile) {
      setErrorMessage("Please select a PDF file");
      return;
    }

    if (!propertyId) {
      setErrorMessage("Property ID is missing");
      return;
    }

    try {
      setUploading(true);
      setErrorMessage("");

      // Upload PDF to Cloudinary
      const pdfUrl = await uploadToCloudinary(selectedFile);

      // Create report with the uploaded PDF
      const payload = {
        propertyId: propertyId,
        pdfUrl,
      };

      const res = await fetch(`${BASE_URL}/reports`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to upload report");
      }

      const result = await res.json();
      console.log("Report uploaded successfully:", result);

      setShowUploadModal(false);
      setSelectedFile(null);
      setSuccessMessage("Report uploaded successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Upload error:", error);
      setErrorMessage(
        `Failed to upload report: ${error.message || "Unknown error"}`,
      );
    } finally {
      setUploading(false);
    }
  };

  /* ================= FILE HANDLING ================= */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setErrorMessage("Please select a PDF file");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage("File size should be less than 10MB");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setSelectedFile(file);
  };


  // Handler for task detail changes
  const handleTaskDetailChange = (
    field: keyof ValuationTaskDetails,
    value: string | Date,
  ) => {
    setValuationTaskDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  console.log("valuationTaskDetails:---", valuationTaskDetails)
  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const requiredFields: (keyof ValuationTaskDetails)[] = [
      "institutionalId",
      "valuationAssignmentDate",
      "professionalCert",
      "principalValuer",
      "regulatoryBody",
    ];

    requiredFields.forEach((field) => {
      if (!valuationTaskDetails[field]?.toString().trim()) {
        const fieldNames: Record<string, string> = {
          institutionalId: "Institutional Client",
          valuationAssignmentDate: "Date of Valuation/Assignment",
          professionalCert: "Professional Certification",
          principalValuer: "Principal Valuer",
          regulatoryBody: "Regulatory Body",
        };
        errors[field] = `${fieldNames[field]} is required`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save valuation details
  const handleSaveValuationDetails = async () => {
    console.log("Saving valuationTaskDetails:", valuationTaskDetails);
    if (!validateForm()) {
      setErrorMessage("Please fill in all required fields");
      return;
    }

    if (!propertyId) {
      setErrorMessage("Property ID is missing");
      return;
    }

    setIsSavingDetails(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      console.log("Saving valuationTaskDetails:", valuationTaskDetails);

      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: valuationTaskDetails,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to save valuation details",
        );
      }

      const result = await response.json();

      // Update local property state
      setProperty((prev) => ({
        ...prev,
        valuationTaskDetails: result.data.valuationTaskDetails,
      }));

      setSuccessMessage("Valuation task details saved successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving valuation details:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to save valuation details",
      );
    } finally {
      setIsSavingDetails(false);
    }
  };

  // Handle opening the collector modal
  const handleOpenCollectorModal = () => {
    setIsCollectorModalOpen(true);
    setSearchQuery("");
  };

  // Handle closing the collector modal
  const handleCloseCollectorModal = () => {
    setIsCollectorModalOpen(false);
    setSelectedCollector("");
    setSearchQuery("");
  };

  // Handle assigning a collector
  const handleAssignCollector = async () => {
    if (!selectedCollector || !propertyId) return;
    setIsAssigning(true);
    setErrorMessage("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      const collector = collectors.find((c) => c.id === selectedCollector);
      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            ...property.valuationDetails,
            assignedCollector: collector
              ? `${collector.firstName} ${collector.lastName}`
              : "",
            collectorId: selectedCollector,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to assign collector");
      }

      const result = await response.json();
      console.log("Assign collector result:", result);
      setProperty(result.data);
      setSuccessMessage("Collector assigned successfully!");
      setIsCollectorModalOpen(false);
      setSelectedCollector("");
      setSearchQuery("");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error assigning collector:", error);
      setErrorMessage("Failed to assign collector. Please try again.");
      setIsCollectorModalOpen(false);
    } finally {
      setIsAssigning(false);
      setIsCollectorModalOpen(false);
    }
  };

  // Handle supervisor approval
  const handleApprove = async () => {
    if (!currentUser || !propertyId) return;
    setIsApproving(true);
    setErrorMessage("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");

      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            ...property.valuationDetails,
            status: "Completed",
            approvedBy: currentUser.name,
            approvedDate: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to approve valuation");
      }

      const result = await response.json();

      console.log("result, result:----", result);
      console.log("Approval result:", result);
      setProperty(result.data);
      setSuccessMessage("Valuation approved successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error approving valuation:", error);
      setErrorMessage("Failed to approve valuation. Please try again.");
    } finally {
      setIsApproving(false);
    }
  };

  const handleAction = (action: string) => {
    switch (action) {
      case "view-report":
        alert("Viewing report...");
        break;
      case "view-data":
        alert("Navigating to Data...");
        break;
      default:
        break;
    }
  };

  // Helper to get institutional client display name — FIX
  const getClientDisplayName = (): string => {
    if (!valuationTaskDetails.institutionalId) return "Select Institutional Client";
    const client = institutionalClients.find(
      (c) => c.id === valuationTaskDetails.institutionalId,
    );
    if (client) return `${client.firstName} ${client.lastName}`;
    if (!isClientsLoaded) return "Loading...";
    return "Select Institutional Client";
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        <Header theme={theme} setTheme={setTheme} logout={logout} />

        <div className="flex">
          <div className="w-64 min-h-screen">
            <Sidebar
              onViewData={() => handleAction("view-data")}
              onViewReport={() => handleAction("view-report")}
            />
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 text-center py-20 w-full mt-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">Loading property details...</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="">
      <Header theme={theme} setTheme={setTheme} logout={logout} />

      <div className="flex">
        <div className="w-64 min-h-screen">
          <Sidebar
            onViewData={() => handleAction("view-data")}
            onViewReport={() => handleAction("view-report")}
          />
        </div>

        <div className="flex-1 ml-4 space-y-6 pl-6 bg-gray-50 min-h-screen">
          <div>
            <div>
              <h1 className="text-lg mt-12 font-semibold text-gray-800">
                Property Details
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                UPI: {property?.upi || "Not assigned"}
              </p>
            </div>
            <div className="flex-1 -mt-16 flex justify-center">
              <div className="my-1">
                <div className="inline-flex items-center bg-white/40 backdrop-blur-lg p-1 rounded-2xl shadow-xl border border-white/30">
                  <button
                    onClick={() => setFilter("data")}
                    className={`px-7 py-3.5 text-sm font-semibold rounded-2xl transition-all duration-300 ${
                      filter === "data"
                        ? "bg-white/90 text-indigo-700 shadow-lg border border-white"
                        : "text-gray-700 hover:bg-white/60 hover:shadow-md"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                        <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                        <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                      </svg>
                      Data
                    </span>
                  </button>

                  <button
                    onClick={() => setFilter("graphics")}
                    className={`px-7 py-3.5 text-sm font-semibold rounded-2xl transition-all duration-300 ${
                      filter === "graphics"
                        ? "bg-white/90 text-indigo-700 shadow-lg border border-white"
                        : "text-gray-700 hover:bg-white/60 hover:shadow-md"
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Visuals
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filter === "data" && (
            <div className="flex space-x-3 mr-5">
              {/* Property Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="col-span-3 lg:col-span-2">
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    {/* Property Header with Image */}
                    <div className="flex flex-col md:flex-row justify-between items-start mb-6">
                      <div className="mb-4 md:mb-0">
                        <h2 className="text-xl font-medium text-gray-800">
                          {property?.ownerName || "Unnamed Property"}
                        </h2>
                        <div className="flex items-center mt-2 space-x-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              property.valuationDetails?.status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : property.valuationDetails?.status ===
                                    "Pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {property.valuationDetails?.status || "New"}
                          </span>
                          {property.valuationDetails?.assignedCollector && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Assigned to:{" "}
                              {property.valuationDetails.assignedCollector}
                            </span>
                          )}
                          {valuationTaskDetails.institutionalId && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {/* FIX: use helper function */}
                              {isClientsLoaded
                                ? (() => {
                                    const client = institutionalClients.find(
                                      (c) => c.id === valuationTaskDetails.institutionalId,
                                    );
                                    return client
                                      ? `Institutional Client: ${client.firstName} ${client.lastName}`
                                      : "Saved ✓";
                                  })()
                                : "Loading..."}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Main Property Image with Click to Open Lightbox */}
                      {hasImages && propertyImages[0] ? (
                        <div className="relative">
                          <button
                            onClick={() => openLightbox(0)}
                            className="w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200 group relative focus:outline-none z-10"
                          >
                            <img
                              src={propertyImages[0]?.url || ""}
                              alt={propertyImages[0]?.label || "Property image"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                              <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                View All Images
                              </span>
                            </div>
                          </button>
                          <div className="text-xs text-gray-500 mt-1 text-center">
                            {propertyImages.length} images available
                          </div>
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                          <div className="text-center">
                            <div className="text-2xl mb-1">📷</div>
                            <span className="text-xs">No Images</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Image Gallery Sidebar - Always visible on Page 1 */}
                    {hasImages && currentPage === 1 && (
                      <div className="mb-6 pb-6 border-b border-gray-200">
                        <h3 className="text-base font-medium text-gray-800 mb-3">
                          Property Images
                        </h3>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                          {propertyImages.slice(0, 16).map((image, index) => (
                            <button
                              key={index}
                              onClick={() => openLightbox(index)}
                              className={`aspect-square rounded overflow-hidden border-2 transition-all hover:border-blue-400 ${
                                index === selectedImageIndex
                                  ? "border-blue-500"
                                  : "border-gray-200"
                              }`}
                            >
                              <img
                                src={image.url}
                                alt={image.label}
                                className="w-full h-full object-cover hover:scale-110 transition-transform duration-200"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src =
                                    "https://via.placeholder.com/100/cccccc/969696?text=Image";
                                }}
                              />
                            </button>
                          ))}
                          {propertyImages.length > 16 && (
                            <button
                              onClick={() => openLightbox(0)}
                              className="aspect-square rounded bg-gray-50 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-blue-300 hover:text-blue-500 transition-colors"
                            >
                              <span className="text-xl">+</span>
                              <span className="text-xs mt-1">
                                {propertyImages.length - 16} more
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Page Content */}
                    <div className="min-h-[400px]">
                      {/* Page 1: Owner & Location Information */}
                      {currentPage === 1 && (
                        <div className="space-y-6">
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">👤</span> Basic Property
                              Information
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <span className="text-sm text-gray-600">
                                  Full Name
                                </span>
                                <p className="font-medium">
                                  {property.ownerName}
                                </p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">
                                  ID Number
                                </span>
                                <p className="font-medium">
                                  {property.ownerIdNumber}
                                </p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">
                                  UPI Number
                                </span>
                                <p className="font-medium">
                                  {property.upi || "Not assigned"}
                                </p>
                              </div>

                              <div>
                                <span className="text-sm text-gray-600">
                                  Plot Number
                                </span>
                                <p className="font-medium">
                                  {property.plotNumber || "Not assigned"}
                                </p>
                              </div>

                              <div>
                                <span className="text-sm text-gray-600">
                                  Phone Number
                                </span>
                                <p className="font-medium">
                                  {property.phone || "Not provided"}
                                </p>
                              </div>
                            </div>
                          </section>

                          <section className="mt-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📋</span> Assignment
                              details
                            </h2>

                            <div className="bg-gray-50 rounded-lg p-4">
                              {/* Valuation Task Details Form */}
                              <section className="mt-6">
                                <div className="bg-gray-50 rounded-lg p-6">
                                  Institutional Client Name *
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Institutional Client Name */}
                                    <div className="space-y-1">
                                      <label className="text-sm font-medium text-gray-700"></label>
                                      <div className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 cursor-not-allowed text-gray-700 flex justify-between items-center">
                                        <span>{getClientDisplayName()}</span>
                                        <UserPlus className="h-4 w-4 text-gray-300" />
                                      </div>
                                    </div>

                                    {/* Date of Valuation/Assignment */}
                                    <div className="space-y-1">
                                      <label className="text-sm font-medium text-gray-700">
                                        Date of Valuation/Assignment
                                      </label>
                                      <input
                                        type="date"
                                        disabled
                                        value={
                                          valuationTaskDetails.valuationAssignmentDate
                                            ? formatDateForInput(valuationTaskDetails.valuationAssignmentDate)
                                            : ""
                                        }
                                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 cursor-not-allowed text-gray-700"
                                      />
                                    </div>

                                    {/* Professional Certification */}
                                    <div className="space-y-1">
                                      <label className="text-sm font-medium text-gray-700">
                                        Professional Certification
                                      </label>
                                      <input
                                        type="text"
                                        disabled
                                        value={valuationTaskDetails.professionalCert || ""}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 cursor-not-allowed text-gray-700"
                                      />
                                    </div>

                                    {/* Principal Valuer */}
                                    <div className="space-y-1">
                                      <label className="text-sm font-medium text-gray-700">
                                        Principal Valuer
                                      </label>
                                      <input
                                        type="text"
                                        disabled
                                        value={valuationTaskDetails.principalValuer || ""}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 cursor-not-allowed text-gray-700"
                                      />
                                    </div>

                                    {/* Assistant Valuer */}
                                    <div className="space-y-1">
                                      <label className="text-sm text-gray-600">
                                        Assistant Valuer
                                      </label>
                                      <input
                                        type="text"
                                        disabled
                                        value={valuationTaskDetails.assistantValuer || ""}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 cursor-not-allowed text-gray-700"
                                      />
                                    </div>

                                    {/* Regulatory Body */}
                                    <div className="space-y-1">
                                      <label className="text-sm font-medium text-gray-700">
                                        Regulatory Body
                                      </label>
                                      <input
                                        type="text"
                                        disabled
                                        value={valuationTaskDetails.regulatoryBody || ""}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 cursor-not-allowed text-gray-700"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </section>

                              {/* Institutional Client Selection Modal */}
                              {isClientModalOpen && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4">
                                  <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                                    <div className="flex justify-between items-center mb-4">
                                      <h3 className="text-lg font-medium text-gray-900">
                                        Select Institutional Client
                                      </h3>
                                      <button
                                        onClick={() => {
                                          setIsClientModalOpen(false);
                                          setClientSearchQuery("");
                                        }}
                                        className="text-gray-400 hover:text-gray-500"
                                      >
                                        <X className="h-6 w-6" />
                                      </button>
                                    </div>

                                    <div className="mb-4">
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Search Client
                                      </label>
                                      <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500"
                                        placeholder="Search by name..."
                                        value={clientSearchQuery}
                                        onChange={(e) =>
                                          setClientSearchQuery(e.target.value)
                                        }
                                      />
                                    </div>

                                    <div className="max-h-64 overflow-y-auto mb-4">
                                      {filteredClients.length > 0 ? (
                                        <ul className="border border-gray-300 rounded-md divide-y divide-gray-200">
                                          {filteredClients.map((client) => (
                                            <li
                                              key={client.id}
                                              className={`px-4 py-3 cursor-pointer hover:bg-indigo-50 ${
                                                valuationTaskDetails.institutionalId ===
                                                client.id
                                                  ? "bg-indigo-100"
                                                  : ""
                                              }`}
                                              onClick={() => {
                                                handleTaskDetailChange(
                                                  "institutionalId",
                                                  client.id,
                                                );
                                                setIsClientModalOpen(false);
                                                setClientSearchQuery("");
                                              }}
                                            >
                                              <div className="font-medium">
                                                {client.firstName}{" "}
                                                {client.lastName}
                                              </div>
                                              {client.email && (
                                                <div className="text-xs text-gray-500">
                                                  {client.email}
                                                </div>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="text-sm text-gray-500 text-center py-4">
                                          No institutional clients found
                                        </p>
                                      )}
                                    </div>

                                    <div className="flex justify-end space-x-3">
                                      <button
                                        onClick={() => {
                                          setIsClientModalOpen(false);
                                          setClientSearchQuery("");
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </section>
                          <TimeTracker property={property} />
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📍</span> Location
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                              {isUganda ? (
                                <>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Country
                                    </span>
                                    <p className="font-medium">Uganda</p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Administrative Region
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.region ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  {property.location?.subRegion && (
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        Sub-region
                                      </span>
                                      <p className="font-medium">
                                        {property.location.subRegion}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      District
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.district ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      County
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.county ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Subcounty
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.subcounty ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Parish
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.parish ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Village
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.village ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  {property.location?.streetAddress && (
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        Address
                                      </span>
                                      <p className="font-medium">
                                        {property.location.streetAddress}
                                      </p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Country
                                    </span>
                                    <p className="font-medium">Rwanda</p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Province
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.province ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      District
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.district ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Sector
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.sector ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Cell
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.cell ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Village
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.village ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  {property.location?.streetAddress && (
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        Address
                                      </span>
                                      <p className="font-medium">
                                        {property.location.streetAddress}
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </section>
                          <section>
                            {property.location?.coordinates && (
                              <div className="bg-gray-50 rounded-lg p-4 flex">
                                <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                  <span className=""></span>🌍 Coordinates
                                </h2>
                                <p className="font-medium ml-52">
                                  {property.location.coordinates.latitude},{" "}
                                  {property.location.coordinates.longitude}
                                </p>
                              </div>
                            )}
                          </section>
                          {/* Valuation Parameters */}
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📐</span> Valuation
                              Parameters
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Land Use */}
                              <div>
                                <label className="text-sm font-medium text-gray-700">
                                  Land Use
                                </label>
                                <p className="font-medium">
                                  {property.landParcel?.landUse || "N/A"}
                                </p>
                              </div>
                              {/* Slope Category */}
                              <div>
                                <label className="text-sm font-medium text-gray-700">
                                  Slope Category
                                </label>
                                <p className="font-medium">
                                  {property.landParcel?.slopeCategory || "N/A"}
                                </p>
                              </div>
                              {/* Land Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Land Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.landParcel?.area || 0}
                                </p>
                              </div>
                              {/* Land Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Land Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.landRate || 0}
                                </p>
                              </div>
                              {/* Main House Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Main House Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.mainHouseArea ||
                                    0}
                                </p>
                              </div>
                              {/* Main House Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Main House Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.mainHouseRate ||
                                    0}
                                </p>
                              </div>
                              {/* Annex Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Annex House Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.annexHouseArea ||
                                    0}
                                </p>
                              </div>
                              {/* Annex Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Annex Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.annexRate || 0}
                                </p>
                              </div>
                              {/* Fence Length */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Fence Length (m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.fenceLength || 0}
                                </p>
                              </div>
                              {/* Fence Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Fence Rate (per m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.fenceRate || 0}
                                </p>
                              </div>
                              {/* Retaining Walls */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Retaining Walls Length (m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.retainingWallsLength || 0}
                                </p>
                              </div>
                              {/* Retaining Wall Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Retaining Wall Rate (per m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.retainingWallRate || 0}
                                </p>
                              </div>
                              {/* Entrance Gate Length */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Entrance Gate Length (m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.entranceGateLength || 0}
                                </p>
                              </div>
                              {/* Gate Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Entrance Gate Rate (per m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.entranceGateRate || 0}
                                </p>
                              </div>
                              {/* Paved Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Paved Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.pavedArea || 0}
                                </p>
                              </div>
                              {/* Paved Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Paved Area Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.pavedAreaRate ||
                                    0}
                                </p>
                              </div>
                              {/* Septic Tank */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Septic Tank Number
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.septicTankNumber || 0}
                                </p>
                              </div>
                              {/* Septic Tank Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Septic Tank Value (per unit)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.septicTankValue ||
                                    0}
                                </p>
                              </div>
                              {/* Garden Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Garden Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.gardenArea || 0}
                                </p>
                              </div>
                              {/* Garden Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Garden Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.gardenRate || 0}
                                </p>
                              </div>
                              {/* Verandah Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Verandah Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.verandahArea || 0}
                                </p>
                              </div>
                              {/* Verandah Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Verandah Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.verandahRate || 0}
                                </p>
                              </div>
                              {/* Landscaping Value */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Landscaping Value ({currency})
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.landscapingValue || 0}
                                </p>
                              </div>
                              {/* Forced Sale Discount */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Forced Sale Discount (%)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.forcedSaleDiscount || 0}
                                </p>
                              </div>
                              {/* Depreciation Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Depreciation Rate (%)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.depreciationRate || 0}
                                </p>
                              </div>
                            </div>
                          </section>
                        </div>
                      )}

                      {/* Page 2: Building Components */}
                      {currentPage === 2 &&
                        property.buildingComponents &&
                        property.valuationDetails && (
                          <div className="space-y-6">
                            {/* Amenities */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">✨</span> Amenities
                              </h2>
                              {property.amenitiesData && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {Object.entries(property.amenitiesData).map(
                                      ([key, value]) => (
                                        <div
                                          key={key}
                                          className="flex items-center space-x-2"
                                        >
                                          <div
                                            className={`w-3 h-3 rounded-full ${
                                              value
                                                ? "bg-green-500"
                                                : "bg-gray-300"
                                            }`}
                                          />
                                          <span className="text-sm capitalize">
                                            {key
                                              .replace("has", "")
                                              .replace(/([A-Z])/g, " $1")
                                              .trim()}
                                          </span>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </section>
                            {/* Building Components */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">🔧</span> Building
                                Components
                              </h2>
                              <div className="space-y-3">
                                {[
                                  {
                                    label: "Foundation Description",
                                    value:
                                      property.buildingComponents
                                        ?.outsideKitchenDescription,
                                  },
                                  {
                                    label: "Roof Description",
                                    value:
                                      property.buildingComponents
                                        ?.waterTankDescription,
                                  },
                                  {
                                    label: " Accomodation Description",
                                    value:
                                      property.buildingComponents
                                        ?.landscapingDescription,
                                  },
                                  {
                                    label: "Walls Description",
                                    value:
                                      property.buildingComponents
                                        ?.soilDrainageDescription,
                                  },
                                  {
                                    label: "Doors & Windows Description",
                                    value:
                                      property.buildingComponents
                                        ?.pvcPipesDescription,
                                  },
                                  {
                                    label: "Floor Description",
                                    value:
                                      property.buildingComponents
                                        ?.washTapsDescription,
                                  },
                                  {
                                    label: "Ceiling Description",
                                    value:
                                      property.buildingComponents
                                        ?.fixturesFittingsDescription,
                                  },
                                ]
                                  .filter((item) => item.value)
                                  .map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-gray-50 rounded-lg p-4"
                                    >
                                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        {item.label}
                                      </h3>
                                      <p className="text-sm text-gray-600 break-words">
                                        {item.value}
                                      </p>
                                    </div>
                                  ))}
                              </div>
                            </section>
                            {/* Building Description */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">🏗️</span> Building
                                Description
                              </h2>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Roof Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.roofCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Walls Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.wallsCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Windows Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.windowsCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Doors Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.doorsCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Floor Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.floorCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Roof Type
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.roofType || "N/A"}
                                  </p>
                                </div>
                                <div className="space-y-3">
                                  <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                      Bedrooms
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {property.mainBuilding?.bedrooms || 0}
                                    </p>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                      Bathrooms
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {property.mainBuilding?.bathrooms || 0}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </section>
                            {/* Adjustments & Variables */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">📊</span> Adjustments &
                                Variables
                              </h2>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Year of Construction
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property?.yearOfConstruction || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Effective Age (years)
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {new Date().getFullYear() -
                                      (property?.yearOfConstruction || 0)}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Annual Depreciation Rate (%)
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.annualDepreciationRate || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Unit Price
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.unitPrice || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Constructed Area (m²)
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.constructedArea ||
                                      0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Original Cost of Construction
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.originalCostOfConstruction || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Maintenance Coefficient
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.maintenanceCoefficient || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Depreciated Value ({currency})
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.depreciatedValue || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Present Value ({currency})
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails?.presentValue ||
                                      0}
                                  </p>
                                </div>
                              </div>
                            </section>
                          </div>
                        )}

                      {/* Page 3: Images */}
                      {currentPage === 3 && property.valuationDetails && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {propertyImages.map((image, index) => (
                              <div
                                key={index}
                                className="bg-white rounded-lg shadow-sm border p-2 cursor-pointer"
                                onClick={() => setZoomImage(image.url)}
                              >
                                <div className="aspect-square w-full rounded overflow-hidden">
                                  <img
                                    src={image.url}
                                    alt={image.label}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                                <p className="text-center text-gray-700 mt-2 text-sm font-medium">
                                  {image.label}
                                </p>
                              </div>
                            ))}
                          </div>
                          {/* Fullscreen Zoom Modal */}
                          {zoomImage && (
                            <div
                              className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                              onClick={() => setZoomImage(null)}
                            >
                              <img
                                src={zoomImage}
                                alt="Zoomed"
                                className="max-w-full max-h-full object-contain"
                              />
                              <button
                                className="absolute top-4 right-4 text-white text-4xl font-bold"
                                onClick={() => setZoomImage(null)}
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Page 4: Financial Analysis & Summary */}
                      {currentPage === 4 && property.valuationDetails && (
                        <>
                          {/* Property location on map*/}
                          {property.mainBuilding && (
                            <div className="mb-6">
                              <h3 className="text-base font-medium text-gray-800 mb-3">
                                🗺️ Property Location
                              </h3>
                              <PropertyMap
                                Upi={property.upi || ""}
                                location={[
                                  property?.location?.coordinates?.latitude ||
                                    1.88596,
                                  property?.location?.coordinates?.longitude ||
                                    30.129675,
                                ]}
                              />
                              <div className="grid grid-cols-1 gap-4 md:gap-8 mt-6">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Google Maps View
                                  </h4>
                                  <iframe
                                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.979567234864!2d${
                                      property?.location?.coordinates
                                        ?.longitude || 0
                                    }!3d${
                                      property?.location?.coordinates
                                        ?.latitude || 0
                                    }!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjM6MTM0!5e0!3m2!1sen!2sng!4v1680879000000!5m2!1sen!2sng`}
                                    width="100%"
                                    height="400"
                                    style={{ border: 0, borderRadius: "8px" }}
                                    allowFullScreen={false}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                  />
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Kigali City Masterplan
                                  </h4>
                                  <iframe
                                    src="https://masterplan.kigalicity.gov.rw/portal/apps/webappviewer/index.html?id=fdd2e30cbc15401d9daee5f68982d755"
                                    width="100%"
                                    height="400"
                                    style={{ border: 0, borderRadius: "8px" }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Kigali City Masterplan"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Property Details */}
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📋</span> Property Details
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Area (sqft)
                                </h3>
                                <p className="text-lg font-medium text-gray-900">
                                  {property?.area}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Age (years)
                                </h3>
                                <p className="text-lg font-medium text-gray-900">
                                  {property?.age || 0}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Condition (1-10)
                                </h3>
                                <div className="flex items-center">
                                  <p className="text-lg font-medium text-gray-900 mr-3">
                                    {(() => {
                                      const conditionMap: Record<
                                        string,
                                        number
                                      > = {
                                        Excellent: 10,
                                        Good: 7,
                                        Average: 5,
                                        Poor: 2,
                                      };
                                      return property.condition;
                                    })()}
                                  </p>
                                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-green-500"
                                      style={{
                                        width: `${
                                          ([
                                            "Excellent",
                                            "Good",
                                            "Average",
                                            "Poor",
                                          ].indexOf(
                                            property.mainBuilding
                                              ?.maintenanceCondition || "",
                                          ) +
                                            1) *
                                          25
                                        }%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Zoning
                                </h3>
                                <p className="text-gray-600">
                                  {property.zoning || "N/A"}
                                </p>
                              </div>
                            </div>
                          </section>
                          {/* Comparative Analysis */}
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📊</span> Comparative
                              Analysis
                            </h2>
                            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Property
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Location
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Area
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Price
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Adjustments
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  <tr className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      Subject Property
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {property.location?.district},{" "}
                                      {property.location?.sector}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {property?.area || 0} m²
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      {currency}{" "}
                                      {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      N/A
                                    </td>
                                  </tr>
                                  <tr className="bg-blue-50">
                                    <td
                                      className="px-4 py-3 text-sm font-medium text-blue-900"
                                      colSpan={3}
                                    >
                                      Comparable Value
                                    </td>
                                    <td className="px-4 py-3 text-lg font-bold text-blue-900">
                                      {currency}{" "}
                                      {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-blue-600">
                                      Market Adjusted
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-4 bg-gray-50 rounded-lg p-4">
                              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                Summary
                              </h3>
                              <p className="text-sm text-gray-600">
                                Based on property data, the comparable Value is
                                estimated at{" "}
                                <span className="font-semibold">
                                  {currency}{" "}
                                  {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                                </span>
                                .
                              </p>
                            </div>
                          </section>
                        </>
                      )}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-8 pt-6 border-t">
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition"
                      >
                        <ChevronLeft size={16} className="mr-1" />
                        Previous
                      </button>
                      <div className="flex items-center space-x-2">
                        {[...Array(totalPages)].map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`w-8 h-8 rounded-full text-sm font-medium transition ${
                              currentPage === i + 1
                                ? "bg-blue-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition"
                      >
                        Next
                        <ChevronRight size={16} className="ml-1" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6 space-y-6">
                    <ValuationSidebarSuperVisor
                      property={property}
                      propertyId={propertyId}
                      mode="supervisor"
                      onValuationComplete={() => {}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {errorMessage}
            </div>
          )}

          {filter === "graphics" && (
            <div className="space-x-3 mr-5">
              <PropertyValuationDashboard property={property} />
              <ValuationVisualization property={property} />
              <PropertyDashboard property={property} />
              <PropertyInfographic property={property} />
              <ValuationTimeline property={property} />
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Lightbox Modal */}
          {isLightboxOpen && hasImages && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
              <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
                {/* Close button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-10 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>

                {/* Zoom controls */}
                <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="text-white text-sm">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 3}
                    className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
                  >
                    <Plus size={20} />
                  </button>
                </div>

                {/* Navigation buttons */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>

                {/* Main image container */}
                <div className="w-full h-full flex items-center justify-center p-4">
                  <div className="max-w-full max-h-full flex items-center justify-center">
                    <img
                      src={propertyImages[selectedImageIndex].url}
                      alt={propertyImages[selectedImageIndex].label}
                      className="max-w-full max-h-full object-contain transition-transform duration-200"
                      style={{ transform: `scale(${zoomLevel})` }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          "https://via.placeholder.com/800x600/cccccc/969696?text=Image+Not+Found";
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronRight size={24} />
                </button>

                {/* Image label */}
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white text-lg font-medium text-center px-4">
                  {propertyImages[selectedImageIndex].label}
                </div>

                {/* Image counter */}
                <div className="absolute top-4 right-16 text-white text-sm">
                  {selectedImageIndex + 1} / {propertyImages.length}
                </div>

                {/* Thumbnails */}
                <div className="absolute bottom-4 left-0 right-0 px-4">
                  <div className="flex overflow-x-auto space-x-2 justify-center py-2 max-w-full">
                    {propertyImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setZoomLevel(1);
                        }}
                        className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                          index === selectedImageIndex
                            ? "border-blue-500"
                            : "border-transparent hover:border-gray-300"
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={image.label}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="ml-32 h-16"></div>
    </div>
  );
};

export default PropertyDetails;